import React, { useState, useEffect, useCallback } from "react";
import { Eye, FileText, Edit } from "lucide-react";
import { OfflineItemService } from "../../../../../services/api/OfflineOrder/offline.service";
import { OrderService } from "../../../../../services/api/order.service";
import { DataTable } from "../../../../DataTable/DataTable";
import AdminOfflineOrderDetails from "./AdminOfflineOrderDetails";
import { EditOfflineOrderModal } from "./EditOfflineOrderModal";
import Bill from "../../../../../shared/Bill";
import { OfflineOrderDetailsData } from "../../../Clinic/ClinicOrder/OfflineOrder/types";

export interface OfllineOrderData {
  orderId: string | number;
  invoiceNumber: string;
  patientName: string;
  patientMobileNumber: string;
  doctorName: string;
  paymentAmount: number;
  paymentType: string;
  cashPaid: number;
  coinsUsed: number;
  onlinePaid: number;
  orderDate: string;
  itemsCount: number;
}

interface ClinicOption {
  id: string;
  name: string;
  profileImage?: string;
}

export const AdminOfflineOrder: React.FC = () => {
  const [offlineOrders, setOfflineOrders] = useState<OfllineOrderData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrderDetails, setSelectedOrderDetails] =
    useState<OfflineOrderDetailsData | null>(null);
  const [showOfflineOrderDetails, setShowOfflineOrderDetails] = useState(false);
  const [showBillPreview, setShowBillPreview] = useState(false);
  const [billOrderId, setBillOrderId] = useState<number | string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrderDetails, setEditingOrderDetails] =
    useState<OfflineOrderDetailsData | null>(null);

  // Filters
  const [clinics, setClinics] = useState<ClinicOption[]>([]);
  const [selectedClinicId, setSelectedClinicId] = useState("");
  const [searchBy, setSearchBy] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
  });

  // Fetch Clinics for dropdown
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const response = await OrderService.getClinicList();
        if (response && response.data) {
          setClinics(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch clinics list:", error);
      }
    };
    fetchClinics();
  }, []);

  // Fetch Offline Orders
  const fetchOfflineOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await OfflineItemService.getOfflineOrderList({
        page: Number(pagination.currentPage),
        pageSize: Number(pagination.itemsPerPage),
        clinicId: selectedClinicId || undefined,
        searchBy: searchBy || undefined,
        searchValue: searchValue || undefined,
        date: selectedDate || undefined,
      });

      if (response && response.success) {
        setOfflineOrders(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          totalItemsCount: response.data.pagination?.totalCount || 0,
          totalPages: response.data.pagination?.totalPages || 1,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch admin offline orders:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    pagination.currentPage,
    pagination.itemsPerPage,
    selectedClinicId,
    searchBy,
    searchValue,
    selectedDate,
  ]);

  useEffect(() => {
    fetchOfflineOrders();
  }, [fetchOfflineOrders]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setPagination((prev) => ({ ...prev, itemsPerPage, currentPage: 1 }));
  };

  const fetchOrderDetails = async (orderId: string | number) => {
    try {
      setIsLoading(true);
      const response = await OfflineItemService.getOfflineOrderDetailsByOrderId(
        String(orderId)
      );
      if (response && response.success) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch offline order details:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (orderId: string | number) => {
    setSelectedOrderId(String(orderId));
    const details = await fetchOrderDetails(orderId);
    if (details) {
      setSelectedOrderDetails(details);
      setShowOfflineOrderDetails(true);
    }
  };

  const handleOpenEditFromTable = async (orderId: string | number) => {
    const details = await fetchOrderDetails(orderId);
    if (details) {
      setEditingOrderDetails(details);
      setShowEditModal(true);
    }
  };

  const handleViewBill = (orderId: string | number) => {
    setBillOrderId(orderId);
    setShowBillPreview(true);
  };

  const handleCloseBillPreview = () => {
    setShowBillPreview(false);
    setBillOrderId(null);
  };

  const handleEditSuccess = (updatedOrderId: string | number) => {
    fetchOfflineOrders();
    // Show updated bill preview as requested
    setBillOrderId(updatedOrderId);
    setShowBillPreview(true);
  };

  const columns = [
    {
      key: "orderId",
      title: "Order ID",
      sortable: true,
      render: (offline: OfllineOrderData) => (
        <span className="font-semibold text-gray-800">
          {offline.invoiceNumber || offline.orderId || "N/A"}
        </span>
      ),
    },
    {
      key: "patientName",
      title: "Patient Name",
      sortable: true,
      render: (offline: OfllineOrderData) => (
        <div className="font-medium text-gray-900">{offline.patientName || "N/A"}</div>
      ),
    },
    {
      key: "patientMobileNumber",
      title: "Mobile Number",
      sortable: true,
      render: (offline: OfllineOrderData) => (
        <div className="text-gray-600">{offline.patientMobileNumber || "N/A"}</div>
      ),
    },
    {
      key: "doctorName",
      title: "Doctor Name",
      sortable: true,
      render: (offline: OfllineOrderData) => (
        <div className="text-gray-700">{offline.doctorName || "N/A"}</div>
      ),
    },
    {
      key: "totalItems",
      title: "Items",
      sortable: true,
      render: (offline: OfllineOrderData) => (
        <div className="text-center font-medium">{offline.itemsCount || 0}</div>
      ),
    },
    {
      key: "totalAmount",
      title: "Amount",
      sortable: true,
      render: (offline: OfllineOrderData) => (
        <div className="font-bold text-gray-900">
          ₹{Number(offline.paymentAmount || 0).toFixed(2)}
        </div>
      ),
    },
    {
      key: "cashPaid",
      title: "Cash Paid",
      sortable: true,
      render: (offline: OfllineOrderData) => (
        <div className="text-gray-700">
          ₹{Number(offline.cashPaid || 0).toFixed(2)}
        </div>
      ),
    },
    {
      key: "onlinePaid",
      title: "Online Paid",
      sortable: true,
      render: (offline: OfllineOrderData) => (
        <div className="text-gray-700">
          ₹{Number(offline.onlinePaid || 0).toFixed(2)}
        </div>
      ),
    },
    {
      key: "orderDate",
      title: "Order Date",
      sortable: true,
      render: (offline: OfllineOrderData) => {
        const date = offline?.orderDate ? new Date(offline.orderDate) : null;
        return (
          <div className="text-gray-600 text-xs">
            {date && !isNaN(date.getTime()) ? date.toLocaleDateString() : "N/A"}
          </div>
        );
      },
    },
    {
      key: "bill",
      title: "Bill",
      render: (offline: OfllineOrderData) => (
        <button
          onClick={() => handleViewBill(offline.orderId)}
          className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
          title="View Bill"
        >
          <FileText className="w-5 h-5" />
        </button>
      ),
    },
    {
      key: "action",
      title: "Action",
      render: (offline: OfllineOrderData) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleViewDetails(offline.orderId)}
            className="p-1.5 text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleOpenEditFromTable(offline.orderId)}
            className="p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded transition-colors"
            title="Edit Order"
          >
            <Edit className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  if (showOfflineOrderDetails && selectedOrderDetails) {
    return (
      <div className="p-4">
        <AdminOfflineOrderDetails
          data={selectedOrderDetails}
          setShowOfflineOrderDetails={setShowOfflineOrderDetails}
          onOrderUpdated={fetchOfflineOrders}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Title */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Admin Offline Orders</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          View and manage in-clinic offline orders across all clinics
        </p>
      </div>

      {/* Bill Preview Modal */}
      {showBillPreview && billOrderId && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto relative shadow-2xl"
            style={{ scrollbarWidth: "thin" }}
          >
            <Bill
              orderId={Number(billOrderId)}
              orderType="OFFLINE"
              onClose={handleCloseBillPreview}
            />
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditModal && editingOrderDetails && (
        <EditOfflineOrderModal
          isOpen={showEditModal}
          order={editingOrderDetails}
          onClose={() => {
            setShowEditModal(false);
            setEditingOrderDetails(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Filters & Search Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {/* Clinic Selector Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Clinic:</span>
            <select
              value={selectedClinicId}
              onChange={(e) => {
                setSelectedClinicId(e.target.value);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              className="border border-gray-300 px-3 py-2 rounded-lg text-sm bg-white focus:ring-green-500 focus:border-green-500 min-w-[180px]"
            >
              <option value="">All Clinics</option>
              {clinics.map((clinic) => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Picker */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Date:</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              className="border border-gray-300 px-3 py-2 rounded-lg text-sm bg-white focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>

        {/* Search Controls */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm w-48 focus:ring-green-500 focus:border-green-500"
          />

          <select
            value={searchBy}
            onChange={(e) => {
              setSearchBy(e.target.value);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm bg-white focus:ring-green-500 focus:border-green-500 w-44"
          >
            <option value="">Search By</option>
            <option value="patient_name">Patient Name</option>
            <option value="patient_mobile_number">Mobile Number</option>
            <option value="invoice_number">Invoice Number</option>
          </select>

          {(selectedClinicId || selectedDate || searchValue || searchBy) && (
            <button
              onClick={() => {
                setSelectedClinicId("");
                setSelectedDate("");
                setSearchValue("");
                setSearchBy("");
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 underline"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Orders Table */}
      <DataTable
        data={offlineOrders as any}
        columns={columns as any}
        itemsPerPage={pagination.itemsPerPage}
        isLoading={isLoading}
        pagination={{
          currentPage: pagination.currentPage,
          setCurrentPage: handlePageChange,
          setPageSize: handleItemsPerPageChange,
          totalPages: pagination.totalPages,
          totalItemsCount: pagination.totalItemsCount,
          pageSize: pagination.itemsPerPage,
        }}
      />
    </div>
  );
};

export default AdminOfflineOrder;
